import { computed, toRefs, reactive, onMounted } from 'vue';
import { mapPersonalDetails } from '@/utils/formatters';
import { useSdk } from './use-mycure';
const ACCOUNTS_SERVICE_NAME = 'accounts';
const PERSONAL_DETAILS_SERVICE_NAME = 'personal-details';
const SERVICE_NAME = 'authentication';
const OTP_SERVICE_NAME = 'otp';

function parseOnboardingFields (dotNotation) {
  const keys = dotNotation.split('.');
  const result = {};

  let currentObj = result;

  for (let i = 0; i < keys.length - 1; i++) {
    currentObj[keys[i]] = {};
    currentObj = currentObj[keys[i]];
  }

  // Set the innermost field as undefined
  currentObj[keys[keys.length - 1]] = Date.now();

  return result;
}

const state = reactive({
  currentUser: null,
  currentUserUID: '',
  otpEnabled: false,
});

export function useAuthentication () {
  const sdk = useSdk();

  state.currentUserUID = computed(() => {
    return state.currentUser?.uid || globalThis.localStorage.getItem('usr:uid');
  });

  const doctorProfessionalDetailsOnboardingDone = computed(() => {
    return !!state.currentUser?.onboarding?.doctorOnboarding?.professionalDetails;
  });

  const doctorPersonalDetailsOnboardingDone = computed(() => {
    return !!state.currentUser?.onboarding?.doctorOnboarding?.personalDetails;
  });

  const doctorWelcomeOnboardingDone = computed(() => {
    return !!state.currentUser?.onboarding?.doctorOnboarding?.welcome;
  });

  const doctorOnboardingDone = computed(() => {
    return (
      doctorProfessionalDetailsOnboardingDone.value &&
      doctorPersonalDetailsOnboardingDone.value
    );
  });

  const isAccountPatient = computed(() => {
    const apps = state.currentUser?.apps || [];
    return apps.includes('easyjoey-patient');
  });

  async function init () {
    const user = await sdk?.currentUser(true);
    state.currentUser = user;
    const personalDetails = await getPersonalDetails(user?.uid);
    const data = {
      ...state.currentUser,
      ...personalDetails,
    };
    state.currentUser = {
      ...mapPersonalDetails(data),
    };
    
    return state.currentUser;
  }

  async function signout () {
    await sdk?.signout();
    state.currentUser = null;
  }

  async function getPersonalDetails (uid) {
    const result = await sdk?.get(PERSONAL_DETAILS_SERVICE_NAME, uid);
    return result;
  }

  async function checkUniqueIdentity (email) {
    const result = await sdk?.create(SERVICE_NAME, {
      identityKey: 'email',
      identity: email,
      action: 'checkUniqueIdentity',
    });

    return result;
  }

  async function generateAPIToken (organization, opts) {
    const result = await sdk?.create(SERVICE_NAME, {
      action: 'generateAPIToken',
      expiresIn: opts?.expiresIn || '1day',
      organization,
    });

    return result?.accessToken;
  }

  async function changePassword (newPassword, oldPassword) {
    const uid = state.currentUser?.uid;
    const result = await sdk?.update(ACCOUNTS_SERVICE_NAME, uid, {
      newPassword,
      oldPassword,
    });

    return result;
  }

  async function sendPasswordResetEmail (email) {
    const result = await sdk?.create(SERVICE_NAME, {
      action: 'sendPasswordResetEmail',
      email,
      acceptUrl: `${globalThis.location.origin}/authentication-management/verify-password-reset`,
    });

    return result;
  }

  async function applyActionCode (code, payload) {
    const result = await sdk?.create(SERVICE_NAME, {
      action: 'applyActionCode',
      code,
      payload,
    });

    return result;
  }

  // Email verification functions
  async function sendVerificationEmail ({ acceptURL }) {
    console.log('Sending verification email with acceptURL:', acceptURL);
    const result = await sdk?.create(SERVICE_NAME, {
      action: 'sendEmailVerification',
      acceptUrl: acceptURL,
    });
    return result;
  }

  async function verifyEmailAddress (opts) {
    const { code } = opts;

    return sdk.create(SERVICE_NAME, {
      action: 'applyActionCode',
      code,
    });
  }

  async function updateOnboarding (field) {
    const uid = state.currentUser?.uid;

    const result = await sdk?.update(ACCOUNTS_SERVICE_NAME, uid, {
      onboarding: {
        ...parseOnboardingFields(field),
      },
    });

    state.currentUser = {
      ...state.currentUser,
      ...result,
    };
  }
  
  // MFA related functions
  
  /**
   * Enable MFA setup for the authenticated user
   * @returns {Object} MFA setup data including QR code and secret
   */
  async function setupMFA(enable = true) {
    const result = await sdk?.create(SERVICE_NAME, {
      action: 'setupMFA',
      enable,
    });
    
    if (result) {
      state.otpEnabled = result.enabled;
    }
    
    return result;
  }
  
  /**
   * Generate OTP setup (QR code and backup codes)
   */
  async function generateOtpSetup() {
    const uid = state.currentUser?.uid;
    if (!uid) throw new Error('User not authenticated');
    
    const result = await sdk?.create(OTP_SERVICE_NAME, {
      action: 'setupOtp',
      userId: uid,
    });
    
    if (result) {
      state.otpEnabled = result.enabled;
    }
    
    return result;
  }
  
  /**
   * Verify MFA setup with TOTP token
   * @param {string} totpToken - The 6-digit TOTP token from authenticator app
   * @returns {Object} Verification result
   */
  async function verifyMFASecret(totpToken) {
    const result = await sdk?.create(SERVICE_NAME, {
      action: 'verifyMFASecret',
      totpToken,
    });
    
    if (result?.verified) {
      state.otpEnabled = true;
    }
    
    return result;
  }
  
  /**
   * Disable MFA for the authenticated user
   * @returns {Object} Result with enabled status
   */
  async function disableMFA() {
    const result = await sdk?.create(SERVICE_NAME, {
      action: 'setupMFA',
      enable: false,
    });
    
    if (result) {
      state.otpEnabled = false;
    }
    
    return result;
  }
  
  /**
   * Get current MFA status for the user
   * @returns {Object} MFA status with enabled and verified states
   */
  async function getMFAStatus() {
    try {
      // Call setupMFA endpoint to get current status
      const result = await sdk?.create(SERVICE_NAME, {
        action: 'setupMFA',
        enable: true,
      });
      
      if (result) {
        // Update local state based on the response
        const isFullySetup = result.enabled && result.verified;
        state.otpEnabled = isFullySetup;
        
        return {
          enabled: result.enabled ?? result.enabeld ?? false,
          verified: result.verified ?? false,
        };
      }
      
      // Default to disabled if we can't determine status
      return {
        enabled: false,
        verified: false,
      };
    } catch (error) {
      console.error('Error getting MFA status:', error);
      return { enabled: false, verified: false };
    }
  }
  
  /**
   * Verify OTP code during login (for 2FA)
   * @param {string} totpToken - The 6-digit TOTP token
   * @returns {Object} Verification result
   */
  async function verifyLoginOtp(totpToken) {
    const result = await sdk?.create(SERVICE_NAME, {
      action: 'verifyOTP',
      totpToken,
    });
    
    return result;
  }
  
  /**
   * Verify and enable OTP for the user
   */
  async function verifyAndEnableOtp(code) {
    const uid = state.currentUser?.uid;
    if (!uid) throw new Error('User not authenticated');
    
    const result = await sdk?.create(OTP_SERVICE_NAME, {
      action: 'verifyOtp',
      userId: uid,
      code,
    });
    
    if (result?.success) {
      state.otpEnabled = true;
    }
    
    return result;
  }
  
  /**
   * Disable OTP for the user
   */
  async function disableOtp() {
    const uid = state.currentUser?.uid;
    if (!uid) throw new Error('User not authenticated');
    
    const result = await sdk?.create(OTP_SERVICE_NAME, {
      action: 'disableOtp',
      userId: uid,
    });
    
    if (result?.success) {
      state.otpEnabled = false;
    }
    
    return result;
  }
  
  /**
   * Generate new backup codes
   */
  async function generateNewBackupCodes() {
    const uid = state.currentUser?.uid;
    if (!uid) throw new Error('User not authenticated');
    
    const result = await sdk?.create(OTP_SERVICE_NAME, {
      action: 'generateBackupCodes',
      userId: uid,
    });
    
    return result;
  }
  
  /**
   * Recover account using backup code
   */
  async function recoverWithBackupCode(code) {
    const uid = state.currentUser?.uid;
    if (!uid) throw new Error('User not authenticated');
    
    const result = await sdk?.create(OTP_SERVICE_NAME, {
      action: 'recoverAccount',
      userId: uid,
      recoveryCode: code,
    });
    
    if (result?.success) {
      state.otpEnabled = false;
    }
    
    return result;
  }

  return {
    doctorProfessionalDetailsOnboardingDone,
    doctorPersonalDetailsOnboardingDone,
    doctorWelcomeOnboardingDone,
    doctorOnboardingDone,
    isAccountPatient,
    ...toRefs(state),
    init,
    sendPasswordResetEmail,
    checkUniqueIdentity,
    changePassword,
    applyActionCode,
    signout,
    updateOnboarding,
    generateAPIToken,
    // Email verification
    sendVerificationEmail,
    verifyEmailAddress,
    // MFA methods
    setupMFA,
    verifyMFASecret,
    disableMFA,
    getMFAStatus,
    // OTP methods
    generateOtpSetup,
    verifyAndEnableOtp,
    disableOtp,
    generateNewBackupCodes,
    verifyLoginOtp,
    recoverWithBackupCode,
  };
};
